import React from 'react';
import * as actionTypes from "./actionTypes";
import {
  getSystemTablesDefinitionAPI,
  deleteSystemTableDefinitionAPI,
  createSystemTableDefinitionAPI,
  updateSystemTableDefinitionAPI,
  duplicateSystemTableDefinitionAPI,
  exportSystemTableDefinitionAPI,
  importSystemTableDefinitionAPI,
  getUserTablesDefinitionAPI,
  deleteUserTableDefinitionAPI,
  createUserTableDefinitionAPI,
  updateUserTableDefinitionAPI,
  duplicateUserTableDefinitionAPI,
  exportUserTableDefinitionAPI,
  importUserTableDefinitionAPI,
} from "@icarius-connection/api";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import { openDialogAction } from "@icarius-common/dialog/actions";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = getLocalizedString("validationError");
      break;
    case "DUPLICATED_DATA":
      errorString = getLocalizedString("cannotExistMoreThanOneTableWithSameNameAndVersionCode");
      break;
    case "IN_USE":
      errorString = getLocalizedString("detailTableCannotBeSelectedBecauseItIsInUse");
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

export const getTablesDefinitionAction = (isSystemTables) => async (dispatch) => {
  dispatch({ type: actionTypes.GET });
  try {
    const apiToUse = isSystemTables ? getSystemTablesDefinitionAPI : getUserTablesDefinitionAPI;

    let response = await apiToUse();
    let data = response.data && response.data.data;
    let codeList = response.data && response.data.codeList;
    let accessTypeList = response.data && response.data.accessTypeList;
    let processList = response.data && response.data.processList;
    let detailTableList = response.data && response.data.detailTableList;
    let userMenuList = response.data && response.data.userMenuList;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_FULFILLED,
      payload: {
        data,
        codeList,
        accessTypeList,
        processList,
        detailTableList,
        userMenuList,
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteTableDefinitionAction = (isSystemTables, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE });
  try {
    const apiToUse = isSystemTables ? deleteSystemTableDefinitionAPI : deleteUserTableDefinitionAPI;

    let response = await apiToUse(dataToSend);
    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: isSystemTables ? getLocalizedString("systemTableDeleted") : getLocalizedString("userTableDeleted"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createTableDefinitionAction = (isSystemTables, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE });
  try {
    const apiToUse = isSystemTables ? createSystemTableDefinitionAPI : createUserTableDefinitionAPI;

    let response = await apiToUse(dataToSend);
    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: isSystemTables ? getLocalizedString("systemTableCreated") : getLocalizedString("userTableCreated"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const updateTableDefinitionAction = (isSystemTables, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.MODIFY });
  try {
    const apiToUse = isSystemTables ? updateSystemTableDefinitionAPI : updateUserTableDefinitionAPI;

    let response = await apiToUse(dataToSend);
    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.MODIFY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.MODIFY_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg:isSystemTables ? getLocalizedString("systemTableModified") : getLocalizedString("userTableModified"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.MODIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const duplicateTableDefinitionAction = (isSystemTables, dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DUPLICATE });
  try {
    const apiToUse = isSystemTables ? duplicateSystemTableDefinitionAPI : duplicateUserTableDefinitionAPI;

    let response = await apiToUse(dataToSend);
    let data = response.data && response.data.data;
    let invalidFields = response.data && response.data.invalidFields;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DUPLICATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DUPLICATE_FULFILLED,
      payload: { data },
    });

    if (invalidFields.length) {
      const msgNode = invalidFields.map((item, index) => {
        return (
          <div key={index}>
            {item}
            {Boolean(index !== (invalidFields.length - 1)) && <br />}
          </div>
        )
      });
      dispatch(openDialogAction({ title: getLocalizedString("fieldsThatCouldNotBeDuplicated"), acceptOnly: true, msg: msgNode }))
    }

    dispatch(openSnackbarAction({ msg:isSystemTables ? getLocalizedString("systemTableDuplicated") : getLocalizedString("userTableDuplicated"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DUPLICATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const exportTableDefinitionAction = (isSystemTables, query) => async (dispatch) => {
  dispatch({ type: actionTypes.IMPORT_EXPORT });
  try {
    const apiToUse = isSystemTables ? exportSystemTableDefinitionAPI : exportUserTableDefinitionAPI;

    const response = await apiToUse(query);
    dispatch({ type: actionTypes.IMPORT_EXPORT_FULFILLED });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.IMPORT_EXPORT_REJECTED, payload: e });
    dispatch(openSnackbarAction({ msg: getErrorStringFromServerError(e), severity: "error" }));
  }
};

export const importTableDefinitionAction = (isSystemTables, query) => async (dispatch) => {
  dispatch({ type: actionTypes.IMPORT_EXPORT });
  try {
    const apiToUse = isSystemTables ? importSystemTableDefinitionAPI : importUserTableDefinitionAPI;

    const response = await apiToUse(query);
    dispatch({ type: actionTypes.IMPORT_EXPORT_FULFILLED });
    dispatch(openSnackbarAction({ msg: getLocalizedString("importedTableDefinitionSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.IMPORT_EXPORT_REJECTED, payload: e });
    dispatch(openSnackbarAction({ msg: getErrorStringFromServerError(e), severity: "error" }));
    return e.response;
  }
};